'use client';

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Rendered in place of `children` once a descendant throws during render. */
  fallback: ReactNode;
  /** Context prefix for the logged error (e.g. the surface being guarded). */
  label?: string;
}

/**
 * Generic render-error boundary. Catches a throw from its subtree — including an
 * async Server Component child whose data load failed (the error is serialized
 * to this client boundary) — and renders `fallback` instead of letting it bubble
 * to the route's `error.tsx` and blank the whole page.
 *
 * `<Suspense>` handles the PENDING state; this handles the FAILED state — wrap
 * the `<Suspense>` in this so one failed section degrades locally. The real
 * error is always logged so a swallowed failure is still diagnosable.
 */
export class ErrorBoundary extends Component<Props, { hasError: boolean }> {
  override state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown) {
    console.error(
      `[ErrorBoundary]${this.props.label ? ` ${this.props.label}:` : ''}`,
      error,
    );
  }

  override render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
